import { setRouteName, resetObjRequests } from 'boot/axios'
import { signUpConfirm } from 'src/boot/dialog-mixin'
import { Platform } from 'quasar'
import { setAVSessionID } from 'src/boot/axios'
import { isSsr } from 'src/constants'
import { setUserToLogRocket } from 'src/boot/log-rocket'
import { useLocationStore } from 'src/stores/location'
import { useAgeVerificationStore } from 'src/stores/age-verification'
import { useThemeStore } from 'src/stores/theme'
import { useLocationDetailStore } from 'src/stores/location-detail'
import { useGuestStore } from 'src/stores/guest'
import { useCustomerStore } from 'src/stores/customer'
import { useAuthStore } from 'src/stores/auth'
import { useShowCaseStore } from 'src/stores/showcase'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'
import {
  initGA,
  trackRoutePageView,
  setGAUserId,
  resolveGAMeasurementId,
} from 'src/utils/ga4'

export default ({ app, router, store, ssrContext }) => {
  const platform = process.env.SERVER ? Platform.parseSSR(ssrContext) : Platform

  // let isFirstPage = false
  let isEAIVLoaded = false
  router.afterEach((routeTo, routeFrom) => {
    const showCaseStore = useShowCaseStore(),
      commonStore = useCommonStore()

    const isMobile = platform?.is?.mobile
    const emitter = app.config?.globalProperties?.emitter
    if (process.env.CLIENT && emitter) {
      emitter.emit('scroll-to-position')
    }

    if (isMobile && routeTo.name != 'Home') {
      if (routeTo.meta.emptyTitle || routeTo.meta.headerTitle) {
        commonStore.setHeaderTitle({
          title: routeTo.meta.headerTitle || '',
        })
      } else {
        commonStore.setHeader({
          categoryButton: true,
          logo: true,
          accountButton: true,
          searchButton: true,
          cartButton: true,
        })
      }
    } else if (isMobile || (!isMobile && routeFrom.name == 'Checkout')) {
      commonStore.resetHeader()
    }

    if (showCaseStore.dialog.location) {
      showCaseStore.updateDialogState({
        dialog: 'location',
        val: false,
      })
    }

    if (routeFrom.name !== routeTo.name && routeFrom.name) {
      let backRoute = commonStore.backRoute

      if (routeTo.meta.clearHistory) {
        commonStore.clearHistory()
      } else if (backRoute && backRoute.name === routeTo.name) {
        commonStore.popFromHistory()
      } else if (!routeFrom.meta.disableHistory) {
        commonStore.addInHistory(routeFrom)
      }
    }

    commonStore.setState({
      key: 'currentRoute',
      value: routeTo,
    })

    // We define disallowed base paths where we only want to allow '/discount/:id', not plain '/discount' or '/discount/'
    const baseDisallowPaths = ['/discount']
    const isBaseDisallowed = baseDisallowPaths.some(
      (p) => routeTo.path === p || routeTo.path === `${p}/`
    )
    if (process.env.CLIENT && !isBaseDisallowed) {
      trackRoutePageView(
        resolveGAMeasurementId(commonStore.businessConfigurations),
        {
          path: routeTo.fullPath || routeTo.path,
          routeName: routeTo.name,
        }
      )
    }
  })

  router.beforeEach(async (routeTo, routeFrom, next) => {
    const locationStore = useLocationStore(),
      ageVerificationStore = useAgeVerificationStore(),
      guestStore = useGuestStore(),
      authStore = useAuthStore(),
      showCaseStore = useShowCaseStore(),
      persistedStore = usePersistedStore(),
      commonStore = useCommonStore()

    const customer = useCustomerStore()
    let allowedToEnter = true,
      isLoggedIn = false,
      customerExists = false,
      isFirstPage = commonStore.isFirstPage

    resetObjRequests()
    if (
      routeTo.name != 'Callback' &&
      ((isSsr && process.env.SERVER) ||
        (!isSsr && !isFirstPage) ||
        routeFrom.name === 'Callback')
    ) {
      // isFirstPage = true
      commonStore.setState({ key: 'isFirstPage', value: true })

      // await authStore.signinSilent()
      await authStore.mapUser()
      await persistedStore.getToken()

      let accessToken = persistedStore.apiAccessToken
      if (!accessToken) {
        commonStore.setState({ key: 'appLoaded', value: true })
        return next()
      }

      setUserToLogRocket && setUserToLogRocket()
      await commonStore.getConfiguration()

      if (process.env.CLIENT) {
        initGA(resolveGAMeasurementId(commonStore.businessConfigurations))
      }

      let modeInfo = getWebsiteModeInfo()
      if (modeInfo.isShow)
        showCaseStore.updateDialogState({
          dialog: modeInfo.dialog,
          val: true,
        })

      const themeStore = useThemeStore()
      await themeStore.getTheme()

      let tenantFound = commonStore.tenantFound,
        storeIsActive = commonStore.storeIsActive

      if (
        !(tenantFound && storeIsActive) &&
        modeInfo.value === 3 &&
        modeInfo.isCodeValidated === true
      ) {
        commonStore.setState({ key: 'appLoaded', value: true })
        return next()
      }

      isLoggedIn = authStore.isLoggedIn

      if (isLoggedIn) {
        await authStore.getUserInfo()
        customerExists = await authStore.checkCustomerExits()
        if (process.env.CLIENT && customerExists) {
          const userId =
            (authStore.currentUser && authStore.currentUser.userId) ||
            authStore.currentUserIdentity
          if (userId) setGAUserId(userId)
        }
        if (customerExists) {
          await customer.getCustomer()

          // check for age verification if already verified before login
          const customerInfo = customer.customer
          const eaivPersistedSessionId = persistedStore.eaivPersistedSessionId
          const businessConfigurations = commonStore.businessConfigurations
          if (
            !customerInfo.isVerifiedOnline &&
            eaivPersistedSessionId &&
            businessConfigurations?.isStoreAgeRestricted &&
            businessConfigurations?.enableAgeRestrictedBrowsing
          ) {
            await customer.mapAgeVerification(eaivPersistedSessionId)
          }
        }
        // store.dispatch('wishlist/getAllWishlist')
        guestStore.resetSlots()
      }

      const businessConfigurations = commonStore.businessConfigurations,
        showEAIVPage = persistedStore.showEAIVPage,
        customerInfo = customer?.customer,
        preferredLocationID = customerInfo?.preferredLocationID,
        preferredFulfillmentMethodID =
          customerInfo?.preferredFulfillmentMethodID,
        persistedLocation = persistedStore.currentLocation,
        persistedFulfillment = persistedStore.selectedFulfillment,
        persistedZipCode = persistedStore.selectedZipCode

      /* EAIV: Show age verification page */
      if (
        !isLoggedIn &&
        businessConfigurations?.isStoreAgeRestricted &&
        showEAIVPage === null
      ) {
        ageVerificationStore.resetVerificationStatus()
        persistedStore.setField({
          field: 'showEAIVPage',
          value: true,
        })
      } else if (
        !businessConfigurations?.isStoreAgeRestricted &&
        showEAIVPage != null
      ) {
        ageVerificationStore.resetVerificationStatus()
        persistedStore.setField({
          field: 'showEAIVPage',
          value: null,
        })
      }

      /* EAIV: Show age verification page */

      const guestCartNumber = guestStore.guestCartNumber,
        verifiedGuestCustomerId = guestStore.verifiedGuestCustomerId
      if (
        isLoggedIn &&
        customerExists &&
        ((guestCartNumber && !verifiedGuestCustomerId) ||
          verifiedGuestCustomerId == customerInfo.customerID) &&
        persistedLocation?.locationId &&
        persistedFulfillment?.fulfillmentId
      ) {
        const locationDetailStore = useLocationDetailStore()
        await locationDetailStore
          .fetchLocation({
            locationId: persistedLocation.locationId,
          })
          .then(async (data) => {
            if (data?.locationId && data.showSetPreffered) {
              let selectedFulfillment = data.fulFillments.find(
                (fulFillment) =>
                  fulFillment.fulfillmentMethodID ===
                  persistedFulfillment?.fulfillmentId
              )
              if (selectedFulfillment) {
                await persistedStore.setServiceLocation({
                  location: {
                    ...data,
                    customerZip:
                      selectedFulfillment.codeName === 'sd'
                        ? customerInfo.zipCode ||
                          persistedStore.locationOrShippingZip ||
                          ''
                        : '',
                  },
                  fulfillment: selectedFulfillment,
                })
                locationStore.setCurrentLocationDetail(data)
              }
            }
          })
          .catch(() => {})
      } else if (
        isLoggedIn &&
        customerExists &&
        (persistedLocation?.locationId !== preferredLocationID ||
          persistedFulfillment?.fulfillmentId !==
            preferredFulfillmentMethodID ||
          !persistedStore.locationOrShippingZip)
      ) {
        const locationDetailStore = useLocationDetailStore()
        await locationDetailStore
          .fetchLocation({
            locationId: preferredLocationID || persistedLocation?.locationId,
          })
          .then(async (data) => {
            if (data?.locationId && data.showSetPreffered) {
              let selectedFulfillment = data.fulFillments.find(
                (fulFillment) =>
                  fulFillment.fulfillmentMethodID ===
                  (preferredFulfillmentMethodID ||
                    persistedFulfillment?.fulfillmentId)
              )
              if (selectedFulfillment) {
                await persistedStore.setServiceLocation({
                  location: {
                    ...data,
                    customerZip:
                      selectedFulfillment.codeName === 'sd'
                        ? customerInfo.zipCode ||
                          persistedStore.locationOrShippingZip ||
                          ''
                        : '',
                  },
                  fulfillment: selectedFulfillment,
                })
                locationStore.setCurrentLocationDetail(data)
              }

              if (
                persistedLocation &&
                persistedFulfillment &&
                preferredLocationID &&
                preferredFulfillmentMethodID &&
                (persistedLocation?.locationId !== preferredLocationID ||
                  persistedFulfillment?.fulfillmentId !==
                    preferredFulfillmentMethodID)
              ) {
                locationStore.setAsMyStore({
                  location: persistedLocation,
                  fulfillment: persistedFulfillment,
                  selectedZipCode: persistedZipCode,
                  forceCompare: true,
                })
              }
            }
          })
          .catch(() => {})
      }
    } else if (
      !platform?.is?.cordova &&
      routeTo.name == 'Callback' &&
      !Object.keys(routeTo.query).length
    ) {
      allowedToEnter = false
      let authRedirectUri = persistedStore.authRedirectUri || '/'
      const resolved = await router.resolve(authRedirectUri)
      if (resolved) next(resolved)
      else next({ path: authRedirectUri })
    } else {
      // store.commit('common/COUNT_DOWN_CHANGE', 0)

      commonStore.showPWAInstallPopup &&
        commonStore.updateState({
          key: 'showPWAInstallPopup',
          val: false,
        })

      setRouteName(routeTo.name)
    }

    /* EAIV: Set session Id in the header */
    if (!isEAIVLoaded) {
      const businessConfigurations = commonStore.businessConfigurations,
        eaivPersistedSessionId = persistedStore.eaivPersistedSessionId,
        eaivPersistedVerificationSuccess =
          persistedStore.eaivPersistedVerificationSuccess,
        customerInfo = customer.customer
      if (
        businessConfigurations?.isStoreAgeRestricted &&
        businessConfigurations?.enableAgeRestrictedBrowsing &&
        ((!isLoggedIn && eaivPersistedVerificationSuccess) ||
          (isLoggedIn && customerInfo.isVerifiedOnline)) &&
        eaivPersistedSessionId
      )
        setAVSessionID(eaivPersistedSessionId)
      isEAIVLoaded = true
    }
    /* EAIV: Set session Id in the header */

    routeTo.matched.some((record) => {
      // check if there is meta data
      let currentLocationId = persistedStore.currentLocationId
      isLoggedIn = authStore.isLoggedIn
      customerExists = authStore.customerExists

      if (
        routeTo?.path === '/loyalty-program' &&
        (!isLoggedIn || !commonStore.storeHasLnsSubscription)
      ) {
        next({
          name: 'Home',
        })
      } else if ('meta' in record) {
        // ------------------------------------------------------------
        // check if user needs to be logged in to access this page
        if (
          'requiresAuth' in record.meta &&
          record.meta.requiresAuth &&
          !isLoggedIn
        ) {
          // this route requires auth, check if user is logged in
          // if not, redirect to Home page.
          // User is not logged in, redirect to signin page
          allowedToEnter = false
          next({
            name: 'Home',
          })

          showCaseStore.updateDialogState({
            dialog: 'accountDrawer',
            val: true,
          })

          persistedStore.setField({
            field: 'authRedirectUri',
            value: routeTo.fullPath,
          })

          // authStore.signIn()
        } else if (
          'requiresCustomer' in record.meta &&
          record.meta.requiresCustomer &&
          !customerExists &&
          isLoggedIn
        ) {
          allowedToEnter = false
          if (
            routeFrom.name &&
            'requiresCustomer' in routeTo.meta &&
            routeTo.meta.requiresCustomer
          ) {
            signUpConfirm.call(router.app)
          } else
            next({
              name: 'Home',
            })
        } else if (
          'requiresLocation' in record.meta &&
          record.meta.requiresLocation &&
          !currentLocationId
        ) {
          allowedToEnter = false
          if (routeFrom.name != 'FindStore')
            next({
              name: 'FindStore',
              query: { redirectFrom: routeTo.fullPath },
            })
        }
      }
    })

    !commonStore.appLoaded &&
      commonStore.setState({ key: 'appLoaded', value: true })

    // go to the requested page
    if (allowedToEnter) next()
  })

  function getWebsiteModeInfo() {
    const persistedStore = usePersistedStore()
    const activeMode = persistedStore.activeMode,
      isCodeValidated = persistedStore.isBetaAccessCodeValidated,
      statusMap = {
        1: {
          dialog: 'OfflineModeDialog',
          value: 1,
          isShow: true,
        },
        2: {
          dialog: 'TestModeDialog',
          value: 2,
          isShow: !isCodeValidated,
          isCodeValidated,
        },
        3: {
          dialog: 'BetaModeDialog',
          value: 3,
          isShow: !isCodeValidated,
          isCodeValidated,
        },
      },
      defaultStatus = {
        dialog: '',
        value: 0,
        isShow: false,
      }
    return statusMap[activeMode] || defaultStatus
  }
}
